// ══════════════════════════════════════════════════════════
//  #FLEET_LOCATIONS_V1 (2026-10-09) — where each unit is, for the Daily Inventory sheet and the GPS map.
//
//  Bill: "time stamp location of unit on this … inventory sheet to just list city off to the far right allowing
//  room for notes. Would be a great reference point."
//
//  api/fleet-locations-sync.js writes two records every 15 minutes / every hour:
//    fleet-locations      { generatedAt, yard, sources, units[{ src, id, vin, name, lat, lng, at, driving, talking,
//                           mph, yard, mi, dir, city, st, work }] }                 — the latest fix of every tracker
//    fleet-location-days  { cols, days:{ 'YYYY-MM-DD': { VIN: [city, st, atYard, mi, dir, at] } } }   — 14 days back
//  These helpers only READ them. Units are matched to the Equipment Master by Serial / VIN (I→1, O/Q→0 folded, the
//  same forgiving match the factory feeds use) — never by the hand-typed tracker name.
// ══════════════════════════════════════════════════════════
import { vinClean } from "./unitValid";

export const LOC_KEY = 'fleet-locations';
export const LOC_DAYS_KEY = 'fleet-location-days';

const loose = (v) => vinClean(v).replace(/I/g, '1').replace(/[OQ]/g, '0');
const pad2 = (n) => String(n).padStart(2, '0');
const localISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const clock = (d) => { let h = d.getHours(); const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12; return `${h}:${pad2(d.getMinutes())} ${ap}`; };

// Serial / VIN → latest fix. A rig with both a Geotab box and a factory feed keeps whichever reported last.
export const locIndex = (blob) => {
  const m = {};
  ((blob && Array.isArray(blob.units)) ? blob.units : []).forEach((u) => {
    if (!u || u.lat == null || u.lng == null) return;
    const k = loose(u.vin);
    if (k.length < 5) return;
    if (!m[k] || String(u.at || '') > String(m[k].at || '')) m[k] = u;
  });
  return m;
};
export const locForVin = (index, vin) => { const k = loose(vin); return (k && index && index[k]) || null; };

// Serial / VIN → where the unit last was on one day (the sheet's stamp).
export const locDayIndex = (daysBlob, dayISO) => {
  const day = daysBlob && daysBlob.days && daysBlob.days[dayISO];
  const m = {};
  if (!day || typeof day !== 'object') return m;
  Object.keys(day).forEach((key) => {
    const s = day[key];
    if (!Array.isArray(s) || key.indexOf(':') >= 0) return;   // "geotab:b12" = a tracker with no VIN typed in — cannot be matched to a unit
    m[loose(key)] = { city: s[0] || '', st: s[1] || '', yard: !!s[2], mi: s[3], dir: s[4] || '', at: s[5] || '' };
  });
  return m;
};
export const locDaysKnown = (daysBlob) => Object.keys((daysBlob && daysBlob.days) || {}).sort();

// "Ennis yard" · "Sherman" · "Warren, MI" · "34 mi NW" (the last one only when the town lookup had no answer)
export const locPlace = (u) => {
  if (!u) return '';
  if (u.yard) return 'Ennis yard';
  if (u.city) return `${u.city}${u.st && u.st !== 'TX' ? `, ${u.st}` : ''}`;
  if (u.mi != null && u.mi !== '') return `${u.mi} mi ${u.dir || ''}`.trim();
  return '';
};
export const locMapUrl = (u) => (u && u.lat != null && u.lng != null) ? `https://www.google.com/maps?q=${Number(u.lat).toFixed(5)},${Number(u.lng).toFixed(5)}` : '';

// When the tracker last reported, written for the day being looked at: "2:05 PM" the same day, "10/6 9:14 AM" otherwise.
export const locWhen = (at, dayISO) => {
  if (!at) return '';
  const d = new Date(at);
  if (isNaN(d.getTime())) return '';
  return localISO(d) === dayISO ? clock(d) : `${d.getMonth() + 1}/${d.getDate()} ${clock(d)}`;
};

// Everything the Daily sheet's GPS cell shows for one unit on one day, or null when there is nothing to show.
//   today      → the live record (re-read every few minutes), so it also knows "moving" and carries a map link
//   past days  → that day's stamp
//   future     → nothing
export const locCell = (vin, dayISO, todayISO, liveIndex, dayIndex) => {
  if (!vin || !dayISO || dayISO > todayISO) return null;
  const k = loose(vin);
  if (k.length < 5) return null;
  const live = dayISO === todayISO ? ((liveIndex && liveIndex[k]) || null) : null;
  const u = live || (dayIndex && dayIndex[k]) || null;
  if (!u) return null;
  const place = locPlace(u);
  if (!place) return null;
  const d = u.at ? new Date(u.at) : null;
  const heard = d && !isNaN(d.getTime()) ? localISO(d) : '';
  const old = !!heard && heard < dayISO;                 // the tracker had not reported that day — this is where it was LAST heard
  const when = locWhen(u.at, dayISO);
  const moving = !!(live && live.driving);
  const far = u.mi != null && !u.yard ? `${u.mi} mi ${u.dir || ''} of the Ennis yard`.replace(/\s+/g, ' ') : '';
  return {
    text: place, when, old, moving, yard: !!u.yard, url: live ? locMapUrl(live) : '',
    title: [place + (u.st && !u.yard && u.st === 'TX' ? ', TX' : ''), far, when ? `${old ? 'tracker last heard' : 'GPS fix'} ${when}` : '',
      moving ? `moving${live.mph ? ` — ${live.mph} mph` : ''}` : '', live && live.talking === false ? 'tracker is not reporting' : '',
      live && live.ride ? `riding with ${live.ride.with || 'a truck'} — trailer's own GPS last heard ${locWhen(live.ride.ownAt, dayISO)}` : '',   // #TRAILER_RIDE_V1
      live ? 'click to open the spot in Google Maps' : ''].filter(Boolean).join('\n'),
  };
};

// ── the GPS map ──
// One of four states per tracker; the map, its tiles and its legend all read these.
export const LOC_STATES = [
  { key: 'moving', label: 'Moving now',        short: 'Moving',        color: '#4ADE80' },
  { key: 'out',    label: 'Parked off-yard',   short: 'Parked out',    color: '#5BB8FF' },
  { key: 'yard',   label: 'At the Ennis yard', short: 'At yard',       color: '#9AA0A8' },
  { key: 'quiet',  label: 'Not reporting',     short: 'Not reporting', color: '#F87171' },
];
const QUIET_DAYS = 3;   // no fix in this long = "not reporting", whatever the tracker's own flag says
export const locState = (u, nowMs) => {
  if (!u) return 'quiet';
  const t = u.at ? new Date(u.at).getTime() : NaN;
  if (u.talking === false || !isFinite(t) || (nowMs || Date.now()) - t > QUIET_DAYS * 86400000) return 'quiet';
  if (u.driving) return 'moving';
  return u.yard ? 'yard' : 'out';
};

// ── #FLEET_LIVE_V1 (2026-10-14) — the fast lane ──
// Bill: "GEO is moving but DOPE has a delay." While a GPS map is open it asks /api/fleet-live every 30 seconds for
// Geotab's own latest status, and these fresh dots are laid over the 5-minute `fleet-locations` record:
//   • a tracker with a newer fix moves to it (moving / parked / mph from Geotab, yard + miles recomputed here);
//   • its town is kept unless it has gone more than LIVE_TOWN_MI from where the town was looked up — then the map
//     says "34 mi NW" until the next 5-minute pull names the new town;
//   • a trailer riding with a truck (TRAILER_RIDE) follows that truck's live dot — unless the trailer's own tracker
//     has spoken since, and then it stands on its own fix.
// A live answer more than 2 minutes old is ignored (the screen just shows the 5-minute record).
const LIVE_TOWN_MI = 5;
const R_MI = 3958.8, rd = (x) => x * Math.PI / 180;
const miles = (a1, o1, a2, o2) => { const dA = rd(a2 - a1), dO = rd(o2 - o1); const h = Math.sin(dA / 2) ** 2 + Math.cos(rd(a1)) * Math.cos(rd(a2)) * Math.sin(dO / 2) ** 2; return 2 * R_MI * Math.asin(Math.min(1, Math.sqrt(h))); };
const point8 = (a1, o1, a2, o2) => { const y = Math.sin(rd(o2 - o1)) * Math.cos(rd(a2)), x = Math.cos(rd(a1)) * Math.sin(rd(a2)) - Math.sin(rd(a1)) * Math.cos(rd(a2)) * Math.cos(rd(o2 - o1));
  return ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][Math.round((((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360) / 45) % 8]; };
export const LIVE_URL = '/api/fleet-live';
export const LIVE_EVERY_MS = 30000;
export const locLiveMerge = (blob, live) => {
  if (!blob || !Array.isArray(blob.units) || !live || !Array.isArray(live.units) || !live.units.length) return blob;
  const liveT = new Date(live.at).getTime();
  if (!isFinite(liveT) || Date.now() - liveT > 120000) return blob;
  const L = {};
  live.units.forEach((x) => { if (x && x.id) L[x.id] = x; });
  const y = blob.yard || {}, yLat = Number(y.lat), yLng = Number(y.lng), yR = Number(y.radiusM) || 500;
  let moved = 0;
  const units = blob.units.map((u) => {
    if (!u || u.src !== 'geotab') return u;
    let base = u, src = L[u.id];
    if (u.ride) {
      if (src && src.at && String(src.at) > String(u.ride.ownAt || '')) { base = { ...u }; delete base.ride; }   // the trailer spoke for itself
      else src = L[u.ride.withId];
    }
    if (!src || !src.at || String(src.at) <= String(u.at || '')) return base;
    const n = { ...base, lat: src.lat, lng: src.lng, at: src.at, driving: !!src.driving, live: true };
    if (typeof src.talking === 'boolean') n.talking = src.talking;
    if (src.mph) n.mph = src.mph; else delete n.mph;
    if (isFinite(yLat) && isFinite(yLng)) {
      const mi = miles(yLat, yLng, n.lat, n.lng);
      n.yard = mi * 1609.344 <= yR;
      n.mi = Math.round(mi * (mi < 10 ? 10 : 1)) / (mi < 10 ? 10 : 1);
      if (n.yard) { n.city = 'Ennis'; n.st = 'TX'; delete n.dir; }
      else {
        n.dir = point8(yLat, yLng, n.lat, n.lng);
        if (base.lat != null && miles(base.lat, base.lng, n.lat, n.lng) > LIVE_TOWN_MI) { delete n.city; delete n.st; delete n.street; }
      }
    }
    moved++;
    return n;
  });
  return { ...blob, units, liveAt: live.at, liveMoved: moved };
};
